let loadedKey: string | undefined;
let loadPromise: Promise<typeof google> | undefined;
let lastLoadFailed = false;
const GOOGLE_LOAD_TIMEOUT_MS = 20_000;

declare global {
  interface Window {
    __map2threejsGoogleReady?: () => void;
    gm_authFailure?: () => void;
  }
}

export function loadGoogleMaps(apiKey: string): Promise<typeof google> {
  if (!apiKey.trim()) return Promise.reject(new Error("A Google Maps API key is required."));
  if (globalThis.google?.maps && !lastLoadFailed) {
    if (loadedKey && loadedKey !== apiKey) {
      return Promise.reject(new Error("Reload the page before testing a different Google Maps key."));
    }
    return Promise.resolve(globalThis.google);
  }
  if (loadPromise) {
    if (loadedKey !== apiKey) return Promise.reject(new Error("A different Google Maps key is already loading."));
    return loadPromise;
  }

  loadedKey = apiKey;
  loadPromise = new Promise<typeof google>((resolve, reject) => {
    const previousAuthFailure = window.gm_authFailure;
    document.querySelector("#map2threejs-google-maps")?.remove();
    let settled = false;
    let timeout: ReturnType<typeof globalThis.setTimeout> | undefined;
    const script = document.createElement("script");
    const cleanup = (): void => {
      if (timeout !== undefined) globalThis.clearTimeout(timeout);
      delete window.__map2threejsGoogleReady;
      if (previousAuthFailure) window.gm_authFailure = previousAuthFailure;
      else delete window.gm_authFailure;
    };
    const fail = (error: Error): void => {
      if (settled) return;
      settled = true;
      cleanup();
      script.remove();
      loadPromise = undefined;
      loadedKey = undefined;
      lastLoadFailed = true;
      reject(error);
    };
    window.gm_authFailure = () => {
      fail(new Error("Google Maps rejected this key. Check its API and browser restrictions."));
    };
    window.__map2threejsGoogleReady = () => {
      globalThis.setTimeout(() => {
        if (settled) return;
        settled = true;
        cleanup();
        lastLoadFailed = false;
        resolve(globalThis.google);
      }, 500);
    };
    script.id = "map2threejs-google-maps";
    script.async = true;
    script.defer = true;
    script.referrerPolicy = "strict-origin-when-cross-origin";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&v=quarterly&loading=async&callback=__map2threejsGoogleReady`;
    script.onerror = () => {
      fail(new Error("Google Maps JavaScript API could not be loaded."));
    };
    timeout = globalThis.setTimeout(() => {
      fail(new Error("Google Maps JavaScript API did not finish loading within 20 seconds."));
    }, GOOGLE_LOAD_TIMEOUT_MS);
    document.head.append(script);
  });
  return loadPromise;
}
